import type { RebatePoint, OverallRebateMetrics } from "@erp/shared-types";

/**
 * Calculate overall rebate metrics from an array of RebatePoint records.
 * When isCumulative is true, uses the last point's cumulative values.
 * When isCumulative is false, sums all incremental values.
 *
 * IMPORTANT: Do NOT use breakdown record volumes for aggregation - they are n-counted
 * (duplicated across rules). Query RebateInvoiceLineItem table directly for accurate volumes.
 *
 * @param points - Array of RebatePoint records to aggregate
 * @param isCumulative - Whether to use cumulative values (from last point) or sum incrementals
 * @returns OverallRebateMetrics with totals and calculated percentage
 */
export function calculateOverallRebateMetrics(
  points: RebatePoint[],
  isCumulative: boolean = false,
): OverallRebateMetrics {
  if (points.length === 0) {
    return {
      totalRebateAmount: 0,
      totalVolume: 0,
      rebateableVolume: 0,
      affectedVolume: 0,
      rebatePercentage: 0,
    };
  }

  let totalRebateAmount: number;
  let totalVolume: number;
  let affectedVolume: number;

  if (isCumulative) {
    // Use cumulative values from the last point
    const lastPoint = points[points.length - 1];
    totalRebateAmount = lastPoint.totalCumulativeRebateAmount || 0;
    totalVolume = lastPoint.totalVolume || 0;
    affectedVolume = lastPoint.totalAffectedVolume || 0;
  } else {
    // Sum all incremental values
    const sums = points.reduce(
      (acc, p) => ({
        totalRebateAmount: acc.totalRebateAmount + p.totalRebateAmount,
        totalVolume: acc.totalVolume + (p.totalVolume || 0),
        affectedVolume: acc.affectedVolume + (p.totalAffectedVolume || 0),
      }),
      {
        totalRebateAmount: 0,
        totalVolume: 0,
        affectedVolume: 0,
      },
    );
    totalRebateAmount = sums.totalRebateAmount;
    totalVolume = sums.totalVolume;
    affectedVolume = sums.affectedVolume;
  }

  const rebatePercentage =
    totalVolume > 0 ? (totalRebateAmount / totalVolume) * 100 : 0;

  return {
    totalRebateAmount,
    totalVolume,
    rebateableVolume: affectedVolume, // Using affectedVolume as proxy for this utility
    affectedVolume,
    rebatePercentage,
  };
}

/**
 * Determine if a rebate rule is conditional based on the new definition.
 *
 * A rule is conditional if ANY of the following are true:
 * - thresholdType === 'growth'
 * - thresholdType === 'volume' AND tiers.length > 1
 * - thresholdType === 'volume' AND first tier threshold > $1
 *
 * Manual rules and flat volume rebates (single tier at $0 or $1) are non-conditional.
 *
 * @param rule - Rule object with thresholdType, isManual, and optional tiers
 * @returns true if the rule is conditional, false otherwise
 */
export function isConditionalRule(rule: {
  thresholdType: string | null;
  isManual: boolean;
  tiers?: Array<{ thresholdValue: string | number }>;
}): boolean {
  // Manual rules are always conditional (amounts are uncertain/one-time events)
  if (rule.isManual) return true;

  // Growth rules are always conditional
  if (rule.thresholdType === "growth") return true;

  // Volume rules: check tiers
  if (rule.thresholdType === "volume" && rule.tiers) {
    // Multiple tiers = conditional
    if (rule.tiers.length > 1) return true;

    // Single tier starting > $1 = conditional (accounts for $0 and $1 being "from dollar one")
    if (rule.tiers.length === 1) {
      const threshold =
        typeof rule.tiers[0].thresholdValue === "string"
          ? parseFloat(rule.tiers[0].thresholdValue)
          : rule.tiers[0].thresholdValue;
      if (threshold > 1) return true;
    }
  }

  // Flat volume starting at $0 or $1 = non-conditional
  return false;
}
